import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface MetricsCardsProps {
  data: any;
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ data }) => {
  const { summary, streaks, timeUtilization } = data;
  const [recurringStats, setRecurringStats] = useState<any>(null);

  useEffect(() => {
    const fetchRecurringStats = async () => {
      try {
        const response = await apiService.getRecurringTasksStats();
        if (response.success) {
          setRecurringStats(response.stats);
        }
      } catch (err) {
        console.log('Failed to fetch recurring stats');
      }
    };
    fetchRecurringStats();
  }, []);

  // Main metrics in horizontal layout
  const mainMetrics = [
    {
      title: 'Total Tasks',
      value: summary.totalTasks,
      icon: '📝',
      gradient: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)'
    },
    {
      title: 'Completed',
      value: summary.completedTasks,
      icon: '✅',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    {
      title: 'Completion Rate',
      value: `${summary.completionRate}%`,
      icon: '📊',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      title: 'Productivity',
      value: summary.productivityScore,
      icon: '🚀',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
    }
  ];

  // Secondary stats
  const secondaryStats = [
    {
      title: 'Current Streak',
      value: `${streaks.currentStreak}`,
      unit: 'days',
      icon: '🔥',
      color: '#f59e0b',
      subtitle: `Best: ${streaks.maxStreak} days`
    },
    {
      title: 'Active Days',
      value: summary.activeDays,
      unit: 'days',
      icon: '📅',
      color: '#06b6d4',
      subtitle: 'In selected period'
    },
    {
      title: 'Time Planned',
      value: timeUtilization.totalEstimatedHours,
      unit: 'hours',
      icon: '⏰',
      color: '#ec4899',
      subtitle: `Avg: ${timeUtilization.averageTaskDuration}min/task`
    },
    {
      title: 'Achievements',
      value: data.achievements?.length || 0,
      unit: 'earned',
      icon: '🏆',
      color: '#f97316',
      subtitle: 'Badges unlocked'
    }
  ];

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Main Metrics - Horizontal */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {mainMetrics.map((metric, index) => (
          <div
            key={index}
            style={{
              background: metric.gradient,
              borderRadius: '16px',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              transition: 'transform 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{metric.icon}</div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {metric.value}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{metric.title}</div>
          </div>
        ))}
      </div>

      {/* Secondary Stats - Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {secondaryStats.map((stat, index) => (
          <div
            key={index}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.25rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
              borderLeft: `4px solid ${stat.color}`,
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: `${stat.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem'
            }}>
              {stat.icon}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '1.75rem', fontWeight: 'bold', color: stat.color }}>
                  {stat.value}
                </span>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{stat.unit}</span>
              </div>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{stat.title}</div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{stat.subtitle}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Recurring Tasks Stats */}
      {recurringStats && recurringStats.totalRecurring > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 1rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🔄 Recurring Tasks Overview
          </h3>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                {recurringStats.activeRecurring}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#a16207' }}>Active Patterns</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                {recurringStats.totalGeneratedTasks}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#a16207' }}>Tasks Generated</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                {recurringStats.completedRecurringTasks}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#a16207' }}>Completed</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                {recurringStats.avgCompletion}%
              </div>
              <div style={{ fontSize: '0.875rem', color: '#a16207' }}>Completion Rate</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsCards;
